import React, { useState, useEffect } from"react";
import PracticeLevelSelector from"./PracticeLevelSelector";
import { useNavigate } from"react-router-dom";
import { 
  Rocket, 
  ChevronRight,
  X 
} from "lucide-react";
import { 
  XMarkIcon 
} from "@heroicons/react/24/outline";
import axios from"axios";
import API_BASE_URL from"../Config/config";
import { useTenant } from"../Context/TenantContext";
import { hasFullAccess } from"../utils/paymentStatus";
import pythonicon from"../Assests/Images/python.png";
import javaicon from"../Assests/Images/java.png";
import nodejsicon from"../Assests/Images/nodejs.png";
import htmlicon from"../Assests/Images/html.png";
import postgresqlicon from"../Assests/Images/postgresql.png";
import cssicon from"../Assests/Images/css.png";
import reacticon from"../Assests/Images/reactjs.png";

const languages = [
 {
 name:"Python",
 desc:"Python is a versatile, general-purpose programming language. It is widely used for web development, data science, automation, and scripting. Its simple syntax makes it beginner-friendly. Python supports multiple programming paradigms including procedural, object-oriented, and functional programming. It has a rich ecosystem of libraries and frameworks.",
 icon: pythonicon,
 },
 {
 name:"Java",
 desc:"Java is a popular object-oriented programming language. It is designed to be platform-independent, enabling code to run anywhere. Java is widely used in enterprise environments, mobile apps, and large systems. It has strong memory management and security features. The language is supported by a vast ecosystem and community.",
 icon: javaicon,
 },
 {
 name:"Node.js",
 desc:"Node.js is a JavaScript runtime built on Chrome’s V8 engine. It allows developers to build scalable backend applications using JavaScript. Node.js uses an event-driven, non-blocking I/O model for efficient performance. It is widely adopted for building APIs, real-time applications, and microservices. The npm ecosystem provides numerous packages for development.",
 icon: nodejsicon,
 },
 // {
 // name:"React.js",
 // desc:"React.js is a popular JavaScript library for building user interfaces. It enables developers to create reusable UI components. React uses a virtual DOM for efficient rendering and updates. It supports single-page applications and mobile apps via React Native. The library is maintained by Facebook and a large open-source community.",
 // icon: reacticon,
 // },
 //{
 // name:"HTML",
 // desc:"HTML stands for HyperText Markup Language and structures web content. It uses tags to define elements like headings, paragraphs, links, and images. HTML is the backbone of every website and works alongside CSS and JavaScript. It is easy to learn and fundamental for web development. HTML5 introduces multimedia and semantic elements.",
 // icon: htmlicon,
 //},
 // {
 // name:"CSS",
 // desc:"CSS (Cascading Style Sheets) is used to style and layout web pages. It controls colors, fonts, spacing, and overall design. CSS allows for responsive designs that adapt to different screen sizes. It supports animations, transitions, and grid/flexbox layouts. CSS works hand-in-hand with HTML to create visually appealing websites.",
 // icon: cssicon,
 // },
 // {
 // name:"PostgreSQL",
 // desc:"PostgreSQL is a powerful open-source relational database system. It supports advanced data types and complex queries. Known for reliability and data integrity, it is widely used in enterprise and web applications. PostgreSQL supports extensibility with custom functions and operators. It also offers strong concurrency and performance optimization features.",
 // icon: postgresqlicon,
 // },
];

const LanguageGrid = () => {
  const { tenant } = useTenant();
  // Step 2.1: Modal state
 const [openPracticeIdx, setOpenPracticeIdx] = useState(null);
 const [showPaymentModal, setShowPaymentModal] = useState(false);
 const [showDetailsModal, setShowDetailsModal] = useState(false);
 const [selectedLanguage, setSelectedLanguage] = useState(null);
 const [selectedLockedLevel, setSelectedLockedLevel] = useState(null);
 const [hasPayment, setHasPayment] = useState(false);
 const [isLoadingPayment, setIsLoadingPayment] = useState(true);

 const navigate = useNavigate();

 // ✅ Check payment status on mount
 useEffect(() => {
 const checkPayment = async () => {
 try {
 const user = JSON.parse(localStorage.getItem("user"));
 const userId = user?.userId;
 
 if (userId) {
 const userHasFullAccess = await hasFullAccess(userId);
 setHasPayment(userHasFullAccess);
 }
 } catch (err) {
 console.error("❌ Failed to check payment:", err);
 setHasPayment(false);
 } finally {
 setIsLoadingPayment(false);
 }
 };
 
 checkPayment();
 }, [hasFullAccess]);

  // ✅ Determine user plan based on payment
  const userPlan = (hasPayment || tenant) ? "premium" : "basic";

  const handleTakeChallenge = (idx) => {
    setSelectedLanguage(languages[idx].name);
    setShowDetailsModal(true);
  };

  const startLearning = () => {
    setShowDetailsModal(false);
    setOpenPracticeIdx(languages.findIndex(l => l.name === selectedLanguage));
  };

  const handleLevelSelect = (level) => {
    setOpenPracticeIdx(null);
    if (!selectedLanguage) return;
    navigate(`/practices/${selectedLanguage.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')}/${level}`);
  };

  const handleUnlockRequired = (level) => {
    // User clicked on a locked level (Advanced/Expert)
    setSelectedLockedLevel(level);
    setShowPaymentModal(true);
  };

  const handleUpgrade = () => {
    navigate('/skillgenpayments');
  };

  const closeModal = () => {
    setShowPaymentModal(false);
    setSelectedLockedLevel(null);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-6">
      <div className="max-w-7xl mx-auto">
        <header className="mb-16 text-center animate-fade-in">
          <h1 className="text-4xl sm:text-6xl font-black text-[#111827] mb-6 tracking-tight">Technology Mastery</h1>
          <p className="text-xl text-[#4B5563] font-medium max-w-2xl mx-auto">
            Deep dive into core technologies. Choose a path and start your journey towards mastery.
          </p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {languages.map((lang, idx) => (
            <div 
              key={idx}
              className="premium-card p-8 flex flex-col group animate-slide-up"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <div className="flex justify-between items-start mb-8">
                <div className="w-20 h-20 bg-[#F8FAFC] rounded-[24px] flex items-center justify-center p-4 border border-purple-50 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-sm">
                   <img src={lang.icon} alt={lang.name} className="max-w-full max-h-full object-contain" />
                </div>
                <div className="px-3 py-1 bg-green-50 text-[#10B981] rounded-full text-[10px] font-black uppercase tracking-widest border border-green-100">
                  Active
                </div>
              </div>

              <h3 className="text-2xl font-black text-[#111827] mb-3 group-hover:text-[#5B21B6] transition-colors">{lang.name}</h3>
              <p className="text-sm text-[#4B5563] font-medium leading-relaxed mb-8 line-clamp-3">
                {lang.desc}
              </p>

              <div className="mt-auto pt-8 border-t border-purple-50 flex items-center justify-between">
                <div className="flex flex-col">
                   <span className="text-[10px] font-black text-[#94A3B8] uppercase tracking-widest">Mastery</span>
                   <span className="text-sm font-bold text-[#111827]">0% Completed</span>
                </div>
                <button 
                  onClick={() => handleTakeChallenge(idx)}
                  className="bg-[#5B21B6] text-white px-6 py-3 rounded-2xl font-bold hover:bg-[#7C3AED] transition-all flex items-center gap-2 shadow-lg shadow-purple-200"
                >
                  Explore <ChevronRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Technology Overview Modal */}
      {showDetailsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in">
          <div className="bg-white rounded-[32px] w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up border border-white/20">
             <div className="relative h-64 bg-gradient-to-br from-[#5B21B6] via-[#7C3AED] to-[#A855F7] p-12 text-white flex flex-col justify-end">
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  className="absolute top-8 right-8 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-2xl flex items-center justify-center transition-colors border border-white/10 text-white"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
                <div className="flex items-center gap-6 mb-4">
                   <div className="w-20 h-20 bg-white p-4 rounded-[24px] shadow-2xl">
                      <img src={languages.find(l => l.name === selectedLanguage)?.icon} alt={selectedLanguage} className="w-full h-full object-contain" />
                   </div>
                   <div>
                      <h2 className="text-4xl font-black mb-1">{selectedLanguage}</h2>
                      <p className="text-purple-100 font-bold opacity-80 uppercase tracking-[3px] text-xs">Path to Mastery</p>
                   </div>
                </div>
             </div>

             <div className="p-12 grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                   <h3 className="text-xl font-black text-[#111827] mb-6 flex items-center gap-2">
                      <span className="w-1.5 h-6 bg-[#5B21B6] rounded-full"></span>
                      What You Will Learn
                   </h3>
                   <div className="space-y-4">
                      {[
                        'Core Fundamentals & Syntax',
                        'Object-Oriented Programming',
                        'Data Structures & Algorithms',
                        'Advanced Features & Best Practices',
                        'Real-world Industry Applications',
                        'Interview Preparation Questions'
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3 p-4 bg-[#F8FAFC] rounded-2xl border border-purple-50 group hover:border-[#5B21B6] transition-all">
                           <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-[#10B981]">
                              <ChevronRight size={14} className="stroke-[3px]" />
                           </div>
                           <span className="text-sm font-bold text-[#4B5563]">{item}</span>
                        </div>
                      ))}
                   </div>
                </div>

                <div className="space-y-8">
                   <div className="premium-card p-8 bg-[#F8FAFC] border-purple-100">
                      <h3 className="text-lg font-black text-[#111827] mb-6">Learning Outcomes</h3>
                      <div className="space-y-6">
                         <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-[#6B7280]">
                            <span>Difficulty</span>
                            <span className="text-[#5B21B6]">Intermediate</span>
                         </div>
                         <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-[#6B7280]">
                            <span>Duration</span>
                            <span className="text-[#5B21B6]">12-15 Hours</span>
                         </div>
                         <div className="flex justify-between items-center text-xs font-bold uppercase tracking-widest text-[#6B7280]">
                            <span>Total Levels</span>
                            <span className="text-[#5B21B6]">4 Levels</span>
                         </div>
                      </div>
                   </div>

                   <button 
                    onClick={startLearning}
                    className="w-full bg-[#5B21B6] text-white py-5 rounded-[20px] font-black text-lg hover:bg-[#7C3AED] transition-all shadow-xl shadow-purple-200 flex items-center justify-center gap-3"
                   >
                    Start Learning <Rocket size={20} />
                   </button>
                </div>
             </div>
          </div>
        </div>
      )}

 {/* --- Modals and Selectors --- */}
 {openPracticeIdx !== null && (
 <div className="fixed inset-0 z-50 flex items-center justify-center bg-bgMain/90 backdrop-blur-sm p-4">
 <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar bg-bgSection rounded-3xl border border-primary/30 shadow-2xl relative p-6">
 <button
 onClick={() => setOpenPracticeIdx(null)}
 className="absolute top-6 right-6 text-muted hover:text-white transition-colors p-2 bg-bgMain rounded-full border border-accentGray/50 z-20"
 >
 ✕
 </button>
          <PracticeLevelSelector
            language={selectedLanguage}
            onSelectLevel={handleLevelSelect}
            onUnlockRequired={handleUnlockRequired}
            onClose={() => setOpenPracticeIdx(null)}
            userPlan={userPlan}
          />
 </div>
 </div>
 )}

      {/* --- Enhanced Payment Modal --- */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-bgMain/80 backdrop-blur-md"
            onClick={closeModal}
          ></div>
          
          <div className="bg-bgSection w-full max-w-lg rounded-[40px] border border-accentGray/20 shadow-[0_30px_100px_rgba(0,0,0,0.2)] relative z-10 overflow-hidden animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
            
            <div className="p-10 text-center">
              <div className="w-20 h-20 bg-primary/10 rounded-[32px] flex items-center justify-center mx-auto mb-8 border border-primary/20">
                <span className="text-4xl">💎</span>
              </div>
              
              <h3 className="text-3xl font-black text-heading mb-4 uppercase tracking-tighter">Premium Required</h3>
              
              <p className="text-body text-lg leading-relaxed mb-10 font-medium">
                The <span className="text-primary font-black uppercase">{selectedLockedLevel}</span> level for <span className="text-primary font-black uppercase">{selectedLanguage}</span> is a premium feature. Upgrade to unlock all levels and AI assistance.
              </p>
              
              <div className="flex flex-col gap-4">
                <button
                  onClick={handleUpgrade}
                  className="btn-primary py-5 rounded-2xl font-black text-lg shadow-xl shadow-primary/30 hover:scale-105 active:scale-95 transition-all uppercase tracking-widest"
                >
                  Unlock Everything
                </button>
                <button
                  onClick={closeModal}
                  className="bg-bgMain text-muted py-4 rounded-2xl font-bold hover:bg-white transition-all uppercase text-sm tracking-widest"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- Details Modal --- */}
      {showDetailsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-bgMain/80 backdrop-blur-md"
            onClick={() => setShowDetailsModal(false)}
          ></div>
          
          <div className="bg-bgSection w-full max-w-2xl rounded-[40px] border border-accentGray/20 shadow-[0_30px_100px_rgba(0,0,0,0.2)] relative z-10 overflow-hidden animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl"></div>
            
            <div className="p-10">
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 p-3 rounded-2xl bg-bgMain border border-accentGray/30 flex items-center justify-center">
                    <img 
                      src={languages.find(l => l.name === selectedLanguage)?.icon} 
                      alt={selectedLanguage} 
                      className="w-full h-full object-contain" 
                    />
                  </div>
                  <h3 className="text-3xl font-black text-heading uppercase tracking-tighter">{selectedLanguage}</h3>
                </div>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  className="w-10 h-10 rounded-full bg-bgMain flex items-center justify-center text-muted hover:text-primary transition-colors border border-accentGray/30"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-primary font-black uppercase text-xs tracking-widest mb-2">Description & Uses</h4>
                  <p className="text-black text-lg leading-relaxed font-medium">
                    {selectedLanguage === "Python" && "Python is a high-level, interpreted programming language known for its clear syntax and versatility. It is widely used in data science, artificial intelligence, web development (Django, Flask), and automation. Its massive library support (NumPy, Pandas, TensorFlow) makes it a top choice for developers worldwide."}
                    {selectedLanguage === "Java" && "Java is an object-oriented, class-based language designed to have as few implementation dependencies as possible. It is the backbone of many enterprise-level applications, Android mobile development, and large-scale backend systems. Its 'write once, run anywhere' philosophy ensures cross-platform compatibility."}
                    {selectedLanguage === "Node.js" && "Node.js is a cross-platform, open-source JavaScript runtime environment that executes JavaScript code outside a web browser. It is optimized for building scalable network applications, real-time systems (chat apps, streaming), and microservices architecture. It uses an asynchronous event-driven model for high performance."}
                  </p>
                </div>
                
                <div>
                  <h4 className="text-primary font-black uppercase text-xs tracking-widest mb-3">Key Points</h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedLanguage === "Python" && [
                      "Simple and Easy to Learn",
                      "Interpreted & Object-Oriented",
                      "Extensive Libraries (SciPy, PyTorch)",
                      "Strong Community Support",
                      "Great for Prototyping",
                      "High Demand in AI/ML"
                    ].map(point => (
                      <li key={point} className="flex items-center gap-2 text-black font-bold">
                        <span className="text-primary text-xl">✓</span> {point}
                      </li>
                    ))}
                    {selectedLanguage === "Java" && [
                      "Platform Independent (JVM)",
                      "Strong Memory Management",
                      "Multi-threaded Environment",
                      "Strict Type Checking",
                      "Enterprise-Grade Security",
                      "Used in Big Data (Hadoop)"
                    ].map(point => (
                      <li key={point} className="flex items-center gap-2 text-black font-bold">
                        <span className="text-primary text-xl">✓</span> {point}
                      </li>
                    ))}
                    {selectedLanguage === "Node.js" && [
                      "Non-blocking I/O Operations",
                      "Single-threaded Event Loop",
                      "Massive Package Ecosystem (NPM)",
                      "JavaScript Everywhere (Full Stack)",
                      "Scalable & Fast Performance",
                      "Ideal for Real-time Apps"
                    ].map(point => (
                      <li key={point} className="flex items-center gap-2 text-black font-bold">
                        <span className="text-primary text-xl">✓</span> {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleTakeChallenge(languages.findIndex(l => l.name === selectedLanguage));
                }}
                className="btn-primary w-full mt-10 py-5 rounded-2xl font-black text-lg shadow-xl shadow-primary/30 hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-widest"
              >
                Start Learning Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
 );
};

export default LanguageGrid;



